import { ErrorView } from "@/components/ErrorView"
import { HBox } from "@/components/HBox"
import { LoadingPage } from "@/components/LoadingPage"
import { VBox } from "@/components/VBox"
import { gql, useMutation } from "@apollo/client"
import { useQuery } from "@apollo/client"
import { LoadingButton, Alert } from "@mui/lab"
import { Box, Button, Container, FormControl, FormControlLabel, InputLabel, MenuItem, Select, Switch, TextField, Typography } from "@mui/material"
import { useEffect, useState } from "react"

import { useParams } from "react-router"
import i18n from '@/i18n'
import moment from "moment"
import { ReleaseCard } from "../../pitches/ReleaseCard"

const MATCHING_RELEASE_QUERY = gql`
  query findMatchingRelease(
    $find: ReleaseQueryInputObjectType
  ) {
    studio {
      releases(
        find: $find
      ) {
        objects {
          id
          slug
          name
          label {
            id
            name
          }
          artists {
            id
            slug
            name
          }
        }
      }
    }  
  }
`

const RELEASE_QUERY = gql`
  query getRelease($releaseId: ID!) {
    studio {
      release(releaseId: $releaseId) {
        id
        slug
        name
        artists {
          name
          slug
          id
        }
        label {
          id
          slug
          name
        }
      }
    }
  }
`

const QUERY = gql`
  query getPitchById($pitchId: ID!) {
    pitch {
      pitch(pitchId: $pitchId) {
        id
        pressText
        streamUrl
        email
        handled
        pitched
        genreName
        source
        release {
          name
          label {
            id
            name
          }
          artists {
            id
            slug
            name
          }
        }
      }
    }
  }
`

const UPDATE_MUTATION = gql`
  mutation upsertPitch($pitchId: ID!, $pressText: String!, $handled: Date, $email: String, $streamUrl: String, $source: String!) {
    upsertPitch(
      pitchId: $pitchId,
      pressText: $pressText,
      email: $email,
      handled: $handled,
      source: $source,
      streamUrl: $streamUrl
    ) {
      pitch {
        id
        streamUrl
        pressText
        handled
        genreName
        source
        release {
          id
          slug
          name
        }
      }
    }
  }
`

export function PitchForm({
  releaseId,
  release: defaultRelease,
  values: defaultValues = { source: 'internal' },
  onUpdateValues = () => console.error('onUpdateValues not implemented')
}) {
  const [values, setValues] = useState(defaultValues)
 
  const updateValues = (newValues) => {
    const updatedValues = {
      ...values,
      ...newValues
    }
    setValues({ ...updatedValues });
    onUpdateValues({ ...updatedValues })
  }

  const { data, loading, refetch } = useQuery(RELEASE_QUERY, {
    variables: {
      releaseId
    },
    skip: !releaseId
  })

  const { matchingReleaseData, loading: matchingRelease, refetch: refetchReleaseMatches } = useQuery(
    MATCHING_RELEASE_QUERY, {
      variables: {
        find: {
          name: values?.releaseName,
          artists: values?.releaseArtists,
          name: values?.labelName
        }
      },
      skip: (!releaseId || !!values?.release)
    }
  )
  
  const matchingReleases = matchingReleaseData?.studio?.releases

  const release = defaultRelease ?? data?.studio?.release

  const disconnectRelease = () => {
    updateValues({
      artistNames: release?.artists?.map(a => a.name)?.join(', ') ?? '',
      releaseName: release?.name,
      labelName: release?.label?.name,
      release: null
    })
  }

  useEffect(() => {
    if (release) {
      updateValues({
        artistNames: release?.artists?.map(a => a.name)?.join(', ') ?? '',
        releaseName: release?.name,
        labelName: release?.label?.name,
        release
      })
    }
  }, [release])

  return (
    <VBox>
      {values?.release ? (
        <Box sx={{ bgcolor: 'rgba(127, 127, 127, .1)' }}>
          <ReleaseCard {...values?.release} />
          {!releaseId && <Button variant="plain" onClick={() => disconnectRelease()} />}
        </Box>
      ) : (
        <>
        <TextField
          type="text"
          name="artist_name"
          label={i18n.t('artist-name')}
          readOnly={!!release}
          defaultValue={release?.artists?.map(a => a.name)?.join(', ') ?? values?.artistNames}
        />
        <TextField
          type="text"
          name="release_name"
          label={i18n.t('release-name')}
          readOnly={!!release}
          defaultValue={release?.name ?? values?.releaseName}
        />
        <TextField
          type="text"
          name="label_name"
          label={i18n.t('label-name')}
          readOnly={!!release}
          defaultValue={release?.label?.name ?? values?.labelName}
        />
        {matchingReleases?.objects?.length > 0 && (
          <>
            <Typography>{i18n.t('matching-releases')}</Typography>
            {matchingReleases.objects.map(matchingRelease => (
              <ReleaseCard 
                sx={{
                  bgcolor: matchingRelease.id === values?.release
                }}
                {...matchingReleaase}
                key={matchingRelease.id}
                onClick={() => setRelease(values?.release)}
              />
            ))}
          </>
        )}
        </>
      )}
      <TextField
        multiline
        fullWidth
        type="email"
        name="email"
        label={i18n.t("email")} 
        onChange={(event) => updateValues({ [event.target.name]: event.target.value })}
        defaultValue={values?.email} 
      />
      <TextField
        multiline
        fullWidth
        rows={8}
        name="pressText"
        label={i18n.t("press-text")} 
        onChange={(event) => updateValues({ [event.target.name]: event.target.value })}
        defaultValue={values?.pressText} 
      />
      <TextField
        multiline
        fullWidth
        type="url"
        name="streamUrl"
        label={i18n.t("stream-url")} 
        onChange={(event) => updateValues({ [event.target.name]: event.target.value })}
        defaultValue={values?.streamUrl} 
      />
      <FormControl sx={{ minWidth: 220 }}>
        <InputLabel>{i18n.t('source')}</InputLabel>
        <Select
          name="source"
          defaultValue={values?.source}
          renderValue={() => <Typography>{values?.source}</Typography>}
          onChange={(event) => {
            updateValues({ [event.target.name]: event.target.value })
          }}
        >
          <MenuItem value="internal">{i18n.t('internal')}</MenuItem>
          <MenuItem value="external">{i18n.t('external')}</MenuItem>
        </Select>
      </FormControl>
      <FormControl>
        <FormControlLabel
          control={<Switch checked={values?.handled} onChange={(event) => updateValues({ handled: event.target.checked ? moment().format('YYYY-MM-DD') : null })} />}
          label={i18n.t('handled')}
        />
      </FormControl>
    </VBox>
  )
}

export function UpdatePitchPage() {
  const { pitchId } = useParams()
  return (
    <Container>
      <UpdatePitchForm pitchId={pitchId} />
    </Container>
  )
}

export function UpdatePitchForm({
  pitchId,
  onClose = () => console.warn('onClosed not implemented'),
  onUpdated = () => console.warn('onUpdated pitch not implemented')
}) {
  if (!pitchId) {
    throw "pitchId is null"
  }
  const [updatePitchMutation, { loading: submitting, error: submitError }] = useMutation(UPDATE_MUTATION);

  const handleSubmit = event => {
    event.preventDefault()
    updatePitchMutation({
      variables: {
        pitchId,
        ...values
      }
    }).then(({ data }) => {
      const pitch = data.upsertPitch.pitch
      if (!pitch) {
        throw new Error('Pitch is undefined')
      }
      if (!pitch.id) {
        throw new Error('Created pitch lacks ID')
      }
      if (!pitch.release) {
        throw new Error('Pitch lacks release')
      }
      if (!pitch.release.name) {
        throw new Error('Pitch lacks release name')
      }
      onUpdated(pitch)
      onClose()
    })
    return false;
  }
  const { data, loading } = useQuery(QUERY, { variables: { pitchId }})
  
  const [values, setValues] = useState(null)

  const handleValueChanged = (newValues) => {
    setValues(newValues)
  }

  useEffect(() => {
    if (data?.pitch?.pitch) {
      setValues(data.pitch.pitch);
    }
  }, [data])

  if (loading) {
    return <LoadingPage />
  }

  if (!data && data?.pitch?.pitch) {
    return <ErrorView />
  }

  if (values) {
    return (
      <form onSubmit={handleSubmit}>
        <VBox>
          {submitError && <Alert color="danger">{i18n.t('an-error-occurred-during-submission')}</Alert>}
          <PitchForm release={values?.release} onUpdateValues={handleValueChanged} values={values} />
          <HBox>
            <LoadingButton
              loading={submitting}
              variant="contained"
              color="error"
              type="submit"
            >
              {i18n.t('save')}
            </LoadingButton>
            <Button
              variant="secondary"
              type="submit"
            >
              {i18n.t('cancel')}
            </Button>
          </HBox>
        </VBox>
      </form>
    )
  }
  
}

export default UpdatePitchPage